import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import {
  Button,
  Input,
  Textarea,
  Label,
  Alert,
  AlertDescription,
  useAuth,
  kmsService,
} from '@aaas/shared';
import { useSpaceDetailQuery } from '../hooks/useSpaceListQuery';
import { SpaceFormData } from '../types';

const SpaceEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, currentWorkspace, idToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<SpaceFormData>({
    name: '',
    description: '',
  });

  // React Query for space details
  const {
    data: space,
    isLoading: fetchLoading,
    error: fetchError,
  } = useSpaceDetailQuery({
    spaceId: id,
    idToken: idToken || undefined,
    enabled: !!id && !!user && !!currentWorkspace,
  });

  // Update form data when space data is loaded
  useEffect(() => {
    if (space) {
      setFormData({
        name: space.name,
        description: space.description || '',
      });
    }
  }, [space]);

  const handleInputChange = (field: keyof SpaceFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!space || !user || !currentWorkspace || !idToken) {
      setError('Space, user, or workspace not found');
      return;
    }

    // Basic validation
    if (!formData.name.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const updatedSpace = await kmsService.updateSpace(idToken, space.id, {
        name: formData.name.trim(),
        description: formData.description?.trim() || undefined,
      });

      // Redirect to the space detail page
      navigate(`/kms/spaces/${updatedSpace.data.id}`);
    } catch (err: unknown) {
      // Handle space update errors gracefully

      // Handle specific error messages
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { status?: number; data?: { message?: string } } };
        if (axiosError.response?.status === 400) {
          setError(axiosError.response.data?.message || 'Space with this code already exists');
        } else {
          setError('Failed to update space');
        }
      } else {
        setError(err instanceof Error ? err.message : 'Failed to update space');
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="w-full p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="w-full p-6">
        <Alert variant="destructive">
          <AlertDescription>
            {fetchError instanceof Error ? fetchError.message : 'Failed to load space'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!space) {
    return (
      <div className="w-full p-6">
        <Alert>
          <AlertDescription>Space not found</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="w-full p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(`/kms/spaces/${space.id}`)} className="p-2">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-3xl font-bold">Edit Space</h1>
      </div>

      {/* Form */}
      <div className="max-w-2xl">
        <div className="bg-white rounded-lg border p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Alert */}
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Space Name */}
            <div className="space-y-2">
              <Label htmlFor="name">
                Space Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter space name..."
                disabled={loading}
                maxLength={255}
                required
              />
              <p className="text-sm text-gray-500">A descriptive name for your business space</p>
            </div>

            {/* Space Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Optional description of the space..."
                disabled={loading}
                maxLength={1000}
                rows={5}
              />
              <p className="text-sm text-gray-500">
                Optional description to help others understand this space (max 1000 characters)
              </p>
            </div>

            {/* Submit Buttons */}
            <div className="flex items-center gap-4 pt-4">
              <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Update Space
                  </>
                )}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/kms/spaces/${space.id}`)}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SpaceEditPage;
